import { nodeToText } from "@corca/core";
import { debounce } from "es-toolkit";
import { action, makeObservable, observable } from "mobx";
import { nanoid } from "nanoid";

import { pyodideManager } from "@/components/pyodide/PyodideManager";
import { getComputedExecutions } from "@/components/suggestions/providers/utils/getPhantomHints";
import { isComputableTableCell } from "@/components/suggestions/providers/utils/getTableCellFormula";
import { ComputedReferenceNodeModel } from "@/models/nodes/ComputedReferenceNodeModel";
import { ComputedResultNodeModel } from "@/models/nodes/ComputedResultNodeModel";
import { createNodeFrom } from "@/models/nodes/createNodeFrom";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { OperatorSymbolNodeModel } from "@/models/nodes/OperatorSymbolNodeModel";
import { SymbolNodeModel } from "@/models/nodes/SymbolNodeModel";
import { isEqualsSign } from "@/models/utils/semanticChecks";

import { healComputedChildren } from "./healComputedChildren";
import {
  collectUnwrapReferenceOps,
  collectUnwrapResultsOps,
  maintainComputedStructure,
} from "./maintainComputedStructure";
import { countEqualsSigns, getComputedResultNode, getRhsNodes } from "./utils";
import {
  collectReferenceNodes,
  evaluateComputedWrapper,
  findCyclicWrapperIds,
  isGroupFocused,
  isStandaloneComputedResult,
  syncReferenceBrackets,
} from "./utils";

import type { DocumentLayout } from "./types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";
import type { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";
import type { ComputedInvalidReason, ComputedState, NodeJson } from "@corca/data";

const RECALC_DEBOUNCE_MS = 400;

/** Forward references converge over multiple passes; cap to stay safe against indirect cycles */
const MAX_PASSES = 10;

/**
 * Tracks computed wrappers and keeps them up to date:
 * - debounced recalculation in document order, triggered by DocumentModel on node changes
 * - transitive invalidation of reference nodes and computed wrappers
 * - disabling the computed flag when the result is edited manually
 * - syncing cached values into reference nodes
 */
export class ComputedNodeManager {
  private doc: DocumentModel;

  enabled = true;

  /** Mirrors the `computed-nodes-ref-on-click` flag, read once per document rather than per node. */
  refOnClickEnabled = false;

  private isRecalculating = false;

  private rerunRequested = false;

  /**
   * Bumped when a tiptap transaction moves equations, so `documentLayout` follows the tiptap
   * document, which MobX does not track.
   */
  private layoutRevision = 0;

  private abortController: AbortController;

  scheduleRecalculation: () => void;

  constructor(doc: DocumentModel) {
    this.doc = doc;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.scheduleRecalculation = debounce(() => this.recalculate(signal), RECALC_DEBOUNCE_MS, {
      signal,
      edges: ["leading", "trailing"],
    });

    makeObservable<typeof this, "documentLayout" | "layoutRevision">(this, {
      enabled: true,
      refOnClickEnabled: true,
      ready: true,
      documentLayout: true,
      layoutRevision: observable,
      markLayoutChanged: action,
    });
  }

  get ready() {
    return this.enabled && pyodideManager.isReady;
  }

  markLayoutChanged(): void {
    this.layoutRevision += 1;
  }

  dispose() {
    this.abortController.abort();
  }

  getState(wrapper: WrapperNodeModel): ComputedState | undefined {
    return wrapper.computed ? getComputedResultNode(wrapper)?.props.state : undefined;
  }

  /** The computed wrapper a `refId` points at, or undefined when it is gone or disabled. */
  private findComputedByRefId(refId: string): WrapperNodeModel | undefined {
    return this.doc.textNode.wrapperNodes.find((w) => w.computed && w.refId === refId);
  }

  get computedWrapperNodes(): WrapperNodeModel[] {
    return this.documentLayout.wrappers.filter((w) => w.computed);
  }

  getTargetState(refId: string): ComputedState | undefined {
    const wrapper = this.findComputedByRefId(refId);
    if (!wrapper) return undefined;
    return this.getState(wrapper);
  }

  getTargetInvalidReason(refId: string): ComputedInvalidReason | undefined {
    const wrapper = this.findComputedByRefId(refId);
    if (!wrapper) return "missing-ref";
    return getComputedResultNode(wrapper)?.props.invalidReason;
  }

  /**
   * The computed node a command acts on: the one under a right-click if the menu was
   * opened that way, otherwise the selected one. Both kinds are atoms, so either way
   * this is exactly one node.
   */
  get selectedComputedNode(): ComputedResultNodeModel | ComputedReferenceNodeModel | undefined {
    const node = this.doc.editor.suggestions.pointerNode ?? this.doc.selection.activeNode;
    if (node?.is(ComputedResultNodeModel) || node?.is(ComputedReferenceNodeModel)) {
      return node;
    }
    return undefined;
  }

  /** The computed wrapper a reference reads its value from. */
  getSourceWrapper(ref: ComputedReferenceNodeModel): WrapperNodeModel | undefined {
    return this.findComputedByRefId(ref.targetRefId);
  }

  /**
   * Replace a reference with its cached value, dropping the reference link.
   * Unlike the drift-driven unwrap in `maintainStructure` this is user-initiated,
   * so it lands on the undo stack and lets recalculation react to the new graph.
   */
  unwrapReference(ref: ComputedReferenceNodeModel): void {
    if (!this.doc.editable || !this.enabled) return;

    const ops: (() => void)[] = [];
    const postOps: (() => void)[] = [];

    this.doc.transact(
      () => {
        collectUnwrapReferenceOps(this.doc, ref, ops, postOps);
        ops.forEach((op) => op());
      },
      `ComputedNodeManager:unwrapReference`,
      { runInAction: true },
    );

    postOps.forEach((op) => op());
  }

  /**
   * Give up the computed link a node carries: a reference releases its value as plain content,
   * a result hands its whole equation back to plain editing. False when `node` is neither, so
   * callers can fall through to their normal handling.
   */
  unlinkComputedNode(node: NodeModel): boolean {
    if (node.is(ComputedReferenceNodeModel)) {
      this.unwrapReference(node);
      return true;
    }

    if (!node.is(ComputedResultNodeModel)) return false;

    const wrapper = this.findComputedByRefId(node.id);
    if (!wrapper) return false;

    this.setComputed(wrapper, false);
    return true;
  }

  /**
   * Move the cursor to the end of the wrapper a reference points at and scroll it
   * into view. Returns false, leaving the cursor untouched, when the target is
   * gone — a reference can outlive the equation it read from.
   */
  goToSource(ref: ComputedReferenceNodeModel): boolean {
    const inlineNode = this.getSourceWrapper(ref)?.inlineNode;
    if (!inlineNode) return false;

    this.doc.input.reset(true);
    this.doc.input.requestSmoothScroll();
    this.doc.cursor.moveTo(inlineNode, inlineNode.nodes.length);

    return true;
  }

  get isInReference(): boolean {
    const cursorNode = this.doc.cursor.node;
    if (!cursorNode) return false;
    return cursorNode.ancestors.some((ancestor) => ancestor.is(ComputedReferenceNodeModel));
  }

  isActiveComputed(inlineNodeId: string): boolean {
    if (this.isInReference) return false;
    const wrapper = this.doc.textNode.wrapperNodes.find(
      (w) => w.computed && w.inlineNode?.id === inlineNodeId,
    );
    return wrapper ? isGroupFocused(this.doc, wrapper) : false;
  }

  isActiveReference(referenceNodeId: string): boolean {
    const ref = this.doc.getNodesByType("computedReference").find((r) => r.id === referenceNodeId);
    if (!ref) return false;
    const wrapper = this.findComputedByRefId(ref.targetRefId);
    return wrapper ? isGroupFocused(this.doc, wrapper) : false;
  }

  isActiveResult(resultNodeId: string): boolean {
    if (this.isInReference) return false;
    const wrapper = this.findComputedByRefId(resultNodeId);
    return wrapper ? isGroupFocused(this.doc, wrapper) : false;
  }

  /**
   * Wrapper ids to highlight for the currently focused node:
   * - cursor inside a reference node → the single target wrapper
   * - cursor inside a calculated result → all deps (variable defs + referenced results)
   * - cursor elsewhere → empty (no highlight)
   * Reference is checked first because a reference always lives inside a result's inline.
   */
  get focusedDependencyWrapperIds(): string[] {
    const cursorNode = this.doc.cursor.node;
    if (!cursorNode) return [];

    const refAncestor = cursorNode.ancestors.find((n): n is ComputedReferenceNodeModel =>
      n.is(ComputedReferenceNodeModel),
    );
    if (refAncestor) {
      const wrapperIdByRefId = new Map<string, string>();
      for (const w of this.doc.textNode.wrapperNodes) {
        if (w.refId) wrapperIdByRefId.set(w.refId, w.id);
      }
      const targetId = wrapperIdByRefId.get(refAncestor.targetRefId);
      return targetId ? [targetId] : [];
    }

    const resultAncestor = cursorNode.ancestors.find((n): n is ComputedResultNodeModel =>
      n.is(ComputedResultNodeModel),
    );
    if (resultAncestor) {
      if (resultAncestor.props.usedWrapperIds) {
        const wrapper = this.doc.cursor.parentWrapperNode;
        return resultAncestor.props.usedWrapperIds.filter((id) => id !== wrapper?.id);
      }
      const wrapper = this.doc.cursor.parentWrapperNode;
      return wrapper ? this.collectReferenceWrapperIds(wrapper) : [];
    }

    return [];
  }

  private collectReferenceWrapperIds(wrapper: WrapperNodeModel): string[] {
    const wrapperIdByRefId = new Map<string, string>();
    for (const w of this.doc.textNode.wrapperNodes) {
      if (w.refId) wrapperIdByRefId.set(w.refId, w.id);
    }
    const ids = new Set<string>();
    for (const ref of collectReferenceNodes(wrapper)) {
      const sourceId = wrapperIdByRefId.get(ref.targetRefId);
      if (sourceId && sourceId !== wrapper.id) ids.add(sourceId);
    }
    return [...ids];
  }

  /** A data cell whose row header is a formula — it computes from the table, not its own content. */
  private isComputableCell(wrapper: WrapperNodeModel): boolean {
    const { tiptap } = this.doc.editor;
    return !!tiptap && isComputableTableCell(this.doc, tiptap, wrapper.id);
  }

  /** A computable cell holding a plain value (no `=`) becomes a standalone computed result. */
  private isStandaloneComputable(wrapper: WrapperNodeModel): boolean {
    return (
      this.isComputableCell(wrapper) && countEqualsSigns(wrapper.inlineNode?.nodes ?? []) === 0
    );
  }

  /**
   * A standalone table-cell result whose row header no longer supplies a formula (e.g. it was
   * deleted). The cell keeps its computed shape but can't recompute, so it is marked invalid rather
   * than unwrapped — restoring the header formula revives it.
   */
  private isOrphanedStandaloneCell(wrapper: WrapperNodeModel): boolean {
    return isStandaloneComputedResult(wrapper) && !this.isComputableCell(wrapper);
  }

  canEnableComputed(wrapper: WrapperNodeModel): boolean {
    if (!this.doc.editable || !this.enabled) return false;

    const nodes = wrapper.inlineNode?.nodes ?? [];
    if (nodes.length === 0) return false;

    // A formula-row data cell is computable regardless of its own (result) content.
    if (this.isComputableCell(wrapper)) return true;

    if (nodes.findIndex(isEqualsSign) === 0) return false;
    if (countEqualsSigns(nodes) > 1) return false;

    const scopeIds = this.documentLayout.scopes.get(wrapper.id) ?? [];
    const executions = getComputedExecutions(this.doc, wrapper, scopeIds);
    if (executions.length === 0) return false;

    return true;
  }

  setComputed(wrapper: WrapperNodeModel, computed: boolean): void {
    if (!this.doc.editable || !this.enabled) return;
    if (computed === wrapper.computed) return;
    if (computed && !this.canEnableComputed(wrapper)) return;

    const postOps: (() => void)[] = [];

    this.doc.transact(
      () => {
        if (computed) {
          // refId persists across toggles so existing references reconnect
          wrapper.updateProps({ computed: true, refId: wrapper.refId ?? nanoid() });
          // Establish the result node up front. Recalc only ever *updates* it, so a
          // deleted result can't be silently respawned — it disables the wrapper.
          this.createResultNode(wrapper);
        } else {
          wrapper.updateProps({ computed: false });
          const ops: (() => void)[] = [];
          collectUnwrapResultsOps(this.doc, wrapper, ops, postOps);
          ops.forEach((op) => op());
        }
      },
      `ComputedNodeManager:setComputed`,
      { runInAction: true, skipComputedRecalc: true },
    );

    postOps.forEach((op) => op());
  }

  async recalculate(signal: AbortSignal): Promise<void> {
    if (!this.enabled) return;

    if (this.isRecalculating) {
      this.rerunRequested = true;
      return;
    }

    this.isRecalculating = true;
    try {
      for (let pass = 0; pass < MAX_PASSES; pass++) {
        // eslint-disable-next-line no-await-in-loop -- passes are sequential by design
        const changed = await this.runPass(signal);
        if (signal.aborted) return;
        if (!changed) break;
      }
    } finally {
      this.isRecalculating = false;
      if (this.rerunRequested) {
        this.rerunRequested = false;
        this.scheduleRecalculation();
      }
    }
  }

  private get documentLayout(): DocumentLayout {
    void this.layoutRevision;
    const { tiptap } = this.doc.editor;
    const scopes = new Map<string, string[]>();

    if (!tiptap) {
      const wrappers = this.doc.textNode.wrapperNodes;
      wrappers.forEach((wrapper, index) => {
        scopes.set(
          wrapper.id,
          wrappers.slice(0, index).map((w) => w.id),
        );
      });
      return { wrappers, scopes };
    }

    const byId = new Map(this.doc.textNode.wrapperNodes.map((w) => [w.id, w]));
    const wrappers: WrapperNodeModel[] = [];
    const seen = new Set<string>();
    let currentScope: string[] = [];
    // Table cells are scoped to their own table, matching `collectPrecedingWrapperIds`: inside a
    // table the scope grows from a snapshot of the outer one, and the snapshot is restored on the
    // way out so cell definitions never leak into wrappers that follow the table.
    let outerScope: string[] | null = null;
    let tableEnd = -1;

    tiptap.state.doc.nodesBetween(0, tiptap.state.doc.content.size, (node, pos) => {
      if (outerScope && pos >= tableEnd) {
        currentScope = outerScope;
        outerScope = null;
      }
      if (node.type.name === "table") {
        // A nested table shares the outermost table's scope rather than opening its own.
        if (!outerScope) {
          outerScope = currentScope;
          currentScope = [...currentScope];
          tableEnd = pos + node.nodeSize;
        }
        return;
      }
      if (node.type.name === "horizontalRule") {
        currentScope = [];
        return;
      }
      if (node.type.name === "inlineExpression") {
        const nodeId = node.attrs.nodeId as string;
        if (!nodeId || seen.has(nodeId)) return;
        const wrapper = byId.get(nodeId);
        if (!wrapper) return;
        seen.add(nodeId);
        scopes.set(nodeId, [...currentScope]);
        wrappers.push(wrapper);
        currentScope.push(nodeId);
      }
    });

    return { wrappers, scopes };
  }

  private async runPass(signal: AbortSignal): Promise<boolean> {
    const { documentLayout } = this;
    let changed = false;

    changed = healComputedChildren(this.doc, documentLayout) || changed;

    const validity = this.applyValidity(documentLayout);

    for (const wrapper of documentLayout.wrappers) {
      if (!wrapper.computed) continue;

      const v = validity.get(wrapper.id);
      if (!v?.valid) {
        this.setState(wrapper, "invalid", v?.reason);
        continue;
      }

      const scopeIds = documentLayout.scopes.get(wrapper.id) ?? [];
      // eslint-disable-next-line no-await-in-loop -- document order matters
      const result = await evaluateComputedWrapper(this.doc, wrapper, scopeIds);
      if (signal.aborted) return false;

      if (result.status === "error") {
        // Keep the cached value, just flag the error
        this.setState(wrapper, "error");
        continue;
      }

      if (result.status === "ok") {
        const refIds = this.collectReferenceWrapperIds(wrapper);
        const usedWrapperIds = [...new Set([...(result.usedWrapperIds ?? []), ...refIds])];
        changed = this.writeResult(wrapper, result.nodes, usedWrapperIds) || changed;
      }

      this.setState(wrapper, "valid");
      changed = this.syncReferences(wrapper) || changed;
    }

    return changed;
  }

  private setState(
    wrapper: WrapperNodeModel,
    state: ComputedState,
    invalidReason?: ComputedInvalidReason,
  ) {
    const result = getComputedResultNode(wrapper);
    if (!result) return;
    if (result.props.state === state && result.props.invalidReason === invalidReason) return;

    this.doc.transact(
      () => {
        result.updateProps({ state, invalidReason });
      },
      `ComputedNodeManager:setState`,
      { runInAction: true, skipComputedRecalc: true, skipHistory: true },
    );
  }

  /**
   * Single-writer maintenance: disable the computed flag when the "=" structure
   * breaks or the user typed alongside the result, unwrapping the result back into
   * plain editable nodes.
   */
  maintainStructure(): boolean {
    if (!this.enabled) return false;

    return maintainComputedStructure(this.doc);
  }

  /**
   * Transitive validity: a wrapper is invalid when it is in a dependency cycle,
   * references a deleted/disabled wrapper, or depends on a wrapper that is itself invalid.
   */
  private applyValidity(
    layout: DocumentLayout,
  ): Map<string, { valid: true } | { valid: false; reason?: ComputedInvalidReason }> {
    const computedByRefId = new Map<string, WrapperNodeModel>();
    for (const wrapper of layout.wrappers) {
      if (wrapper.computed && wrapper.refId) {
        computedByRefId.set(wrapper.refId, wrapper);
      }
    }

    const refsByWrapper = new Map<string, ComputedReferenceNodeModel[]>();
    for (const wrapper of layout.wrappers) {
      refsByWrapper.set(wrapper.id, collectReferenceNodes(wrapper));
    }

    const deps = new Map<string, string[]>();
    const reverseRefs = new Map<string, string[]>();
    for (const wrapper of layout.wrappers) {
      const refTargets = (refsByWrapper.get(wrapper.id) ?? [])
        .map((ref) => computedByRefId.get(ref.targetRefId)?.id)
        .filter(Boolean);
      const prevUsedIds = getComputedResultNode(wrapper)?.props.usedWrapperIds ?? [];
      deps.set(wrapper.id, [...new Set([...refTargets, ...prevUsedIds])]);
      for (const targetId of refTargets) {
        reverseRefs.set(targetId, [...(reverseRefs.get(targetId) ?? []), wrapper.id]);
      }
    }

    const cyclicIds = findCyclicWrapperIds(deps);
    const validity = new Map<
      string,
      { valid: true } | { valid: false; reason?: ComputedInvalidReason }
    >();

    for (const wrapper of layout.wrappers) {
      if (cyclicIds.has(wrapper.id)) {
        validity.set(wrapper.id, { valid: false, reason: "cycle" });
        continue;
      }
      const hasMissingRef = (refsByWrapper.get(wrapper.id) ?? []).some(
        (ref) => !computedByRefId.has(ref.targetRefId),
      );
      if (hasMissingRef) {
        validity.set(wrapper.id, { valid: false, reason: "missing-ref" });
        continue;
      }

      if (this.isOrphanedStandaloneCell(wrapper)) {
        validity.set(wrapper.id, { valid: false, reason: "not-computable" });
        continue;
      }

      validity.set(wrapper.id, { valid: true });
    }

    function markReferencesInvalid(wrapperId: string, reason?: ComputedInvalidReason) {
      const wrapperValidity = validity.get(wrapperId);
      if (wrapperValidity?.valid) return;
      for (const refWrapperId of reverseRefs.get(wrapperId) ?? []) {
        if (validity.get(refWrapperId)?.valid) {
          validity.set(refWrapperId, { valid: false, reason: wrapperValidity?.reason });
          markReferencesInvalid(refWrapperId, reason);
        }
      }
    }

    for (const wrapper of layout.wrappers) {
      markReferencesInvalid(wrapper.id);
    }

    return validity;
  }

  private createResultNode(wrapper: WrapperNodeModel): void {
    const inline = wrapper.inlineNode;
    const { refId } = wrapper;
    if (!inline || !refId || getComputedResultNode(wrapper)) return;

    // The whole cell content becomes the value inside the result node — there is no "LHS =" to skip.
    if (this.isStandaloneComputable(wrapper)) {
      const valueNodes = [...inline.nodes];
      this.doc.batchCreateNodes(() => {
        const valueInline = this.doc.create(InlineNodeModel, valueNodes);
        const result = ComputedResultNodeModel.create(this.doc, {}, valueInline, refId);
        inline.setChildren([result.id]);
        return [valueInline, result];
      });
      return;
    }

    const rhsNodes = getRhsNodes(wrapper);
    const eqIndex = inline.nodes.findIndex(isEqualsSign);
    const keepIds =
      eqIndex === -1
        ? inline.childrenIds
        : inline.childrenIds.slice(0, inline.childrenIds.indexOf(inline.nodes[eqIndex].id) + 1);

    this.doc.batchCreateNodes(() => {
      const valueInline = this.doc.create(InlineNodeModel, rhsNodes);
      const result = ComputedResultNodeModel.create(this.doc, {}, valueInline, refId);

      if (eqIndex === -1) {
        const eqNode = this.doc.create(
          OperatorSymbolNodeModel,
          this.doc.create(SymbolNodeModel, "=", { key: "symbol-equal" }),
          {
            semanticType: ["relation"],
          },
        );
        inline.setChildren([...keepIds, eqNode.id, result.id]);
      } else {
        inline.setChildren([...keepIds, result.id]);
      }

      return [valueInline, result];
    });
  }

  private writeResult(
    wrapper: WrapperNodeModel,
    resultNodes: NodeJson[],
    usedWrapperIds?: string[],
  ): boolean {
    const result = getComputedResultNode(wrapper);
    if (!result) return false;

    const newRhs = nodeToText({
      type: "inline",
      props: {},
      children: resultNodes,
    });

    const nodeText = nodeToText(result.toJSON());

    if (nodeText === newRhs) {
      // Content already current; keep the snapshot and usedWrapperIds in step.
      const snapshotStale = result.props.snapshot !== newRhs;
      const idsStale =
        usedWrapperIds !== undefined &&
        JSON.stringify(result.props.usedWrapperIds) !== JSON.stringify(usedWrapperIds);
      if (snapshotStale || idsStale) {
        this.doc.transact(
          () => {
            result.updateProps({ snapshot: newRhs, usedWrapperIds });
          },
          `ComputedNodeManager:snapshot`,
          { runInAction: true, skipComputedRecalc: true, skipHistory: true },
        );
      }
      return false;
    }

    this.doc.transact(
      () => {
        this.doc.batchCreateNodes(() => {
          const created = resultNodes
            .map((json) => createNodeFrom(json, this.doc, { resetIds: true }))
            .filter((n): n is NodeModel => !!n);
          const valueInline = this.doc.create(InlineNodeModel, created);

          // Repoint the result node at a fresh value inline (LWW), stamping the
          // snapshot and usedWrapperIds in the same transaction.
          result.setChildren([valueInline]);
          result.updateProps({ inlineId: valueInline.id, snapshot: newRhs, usedWrapperIds });

          return [valueInline, ...created];
        });
      },
      `ComputedNodeManager:writeResult`,
      { skipComputedRecalc: true, skipHistory: true },
    );

    return true;
  }

  /**
   * Copy the wrapper's current result into every reference node pointing at it.
   * Invalid references keep their last cached value.
   */
  private syncReferences(wrapper: WrapperNodeModel): boolean {
    if (!wrapper.refId) return false;

    // The cached value is the result node's content; a plain definition without
    // a result node falls back to its loose RHS.
    const result = getComputedResultNode(wrapper);
    if (!result?.inlineNode) return false;
    const { inlineNode } = result;

    const nodeText = nodeToText(result.toJSON());

    const staleRefs = this.doc.getNodesByType("computedReference").filter(
      (ref) =>
        ref.targetRefId === wrapper.refId &&
        ref.hasAncestorType("text") &&
        // Guard against self-references — never sync a reference into its own target
        !ref.ancestors.some((ancestor) => ancestor === wrapper) &&
        // Re-sync when the value we last wrote into this ref differs from the
        // current target value (the snapshot is updated atomically below).
        ref.props.snapshot !== nodeText,
    );

    if (staleRefs.length === 0) return false;

    this.doc.transact(
      () => {
        this.doc.batchCreateNodes(() => {
          const roots: NodeModel[] = [];
          for (const ref of staleRefs) {
            // Same LWW-pointer write as the result node: a brand-new inline node
            // per ref, repointed via `inlineId`, so concurrent syncs converge.
            const valueInline = createNodeFrom(inlineNode, this.doc, { resetIds: true });
            ref.setChildren([valueInline]);
            ref.updateProps({ inlineId: valueInline.id, snapshot: nodeText });
            roots.push(valueInline);
          }
          return roots;
        });
        // The new values are registered only once the batch returns.
        staleRefs.forEach(syncReferenceBrackets);
      },
      `ComputedNodeManager:syncReferences`,
      { skipComputedRecalc: true, skipHistory: true },
    );

    return true;
  }

  getComputedRefLabel(
    node: ComputedResultNodeModel | ComputedReferenceNodeModel,
  ): string | undefined {
    if (node.props.label) return node.props.label;

    const index = this.getComputedRefIndex(node.id);
    return index === undefined ? undefined : `R${index + 1}`;
  }

  getComputedRefIndex(id: string): number | undefined {
    const node = this.doc.nodes.get(id);

    // A result node's id is its own wrapper's refId, so both kinds resolve through refId.
    let refId: string | undefined;
    if (node?.is(ComputedReferenceNodeModel)) refId = node.targetRefId;
    else if (node?.is(ComputedResultNodeModel)) refId = id;
    if (refId === undefined) return undefined;

    const wrapper = this.findComputedByRefId(refId);
    if (!wrapper) return undefined;

    // A wrapper the layout has not seen yet is unnumbered rather than first.
    const index = this.computedWrapperNodes.findIndex((w) => w.id === wrapper.id);
    return index === -1 ? undefined : index;
  }
}
